/**
 * This aligns the video and audio. In the degenerate case, it's just audio.
 */
import { makeAutoObservable } from "mobx";
import SearchState from "./SearchState";
import MusicTrackState from "./components/MusicTrack/MusicTrackState";

export default class Timeline {
  /** @type {SearchState} */
  searchState;

  /** @type {number} */
  audioOffset = 0;

  /** @type {number} */
  position = 0;

  /** @type {boolean} */
  playing = false;

  /** @type {number} */
  videoAnchorPercentage = 0;

  /** @type {number} */
  startTime = 0;

  /** @type {NodeJS.Timeout | null} */
  audioStartTimeout = null;

  /** @type {NodeJS.Timeout | null} */
  videoStartTimeout = null;

  /** @type {boolean} */
  firstPlay = true;

  /** @type {boolean} */
  anchorSet = false;

  /**
   * @param {SearchState} searchState
   */
  constructor(searchState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.searchState = searchState;
  }

  /**
   * @action
   * @param {number} seconds
   */
  setAudioOffset = (seconds) => {
    const oldVideoPosition = this.position - this.videoOffsetFromBeginning;
    this.audioOffset = seconds;
    const newVideoPosition = this.position - this.videoOffsetFromBeginning;
    const diff = newVideoPosition - oldVideoPosition;
    this.setPosition(this.position - diff);

    // if (this.searchState.videoSyncState.isBeingManuallyDragged) {
    this.searchState.changePlaylistVideoSyncData(
      this.searchState.selectedTrack.music.id,
      {
        audioOffset: this.audioOffset,
      },
    );
    // }
  };

  setVideoAnchorPercentage = (videoAnchorPercentage) => {
    this.videoAnchorPercentage = videoAnchorPercentage;
    this.searchState.savedPlaylistData.videoAnchorPercentage =
      this.videoAnchorPercentage;
  };

  get videoDuration() {
    return this.searchState.videoSyncState.videoDuration;
  }

  get trackDuration() {
    if (MusicTrackState.globallyActiveTrack != null) {
      const active = MusicTrackState.globallyActiveTrack;
      if (active.audioBuffer != null) {
        return active.audioBuffer.duration;
      }
      return MusicTrackState.globallyActiveTrack.music.duration;
    }
    return 0;
  }

  get duration() {
    if (this.audioOffset > 0) {
      return Math.max(
        this.videoDuration,
        this.audioOffset + this.trackDuration,
      );
    }

    return Math.max(this.videoDuration - this.audioOffset, this.trackDuration);
  }

  get percentage() {
    return this.position / this.duration;
  }

  get videoOffsetFromBeginning() {
    return this.audioOffset < 0 ? Math.abs(this.audioOffset) : 0;
  }

  get audioOffsetFromBeginning() {
    return this.audioOffset > 0 ? this.audioOffset : 0;
  }

  get videoPercentage() {
    const positionInVideo = this.position - this.videoOffsetFromBeginning;
    return Math.min(1.0, Math.max(0.0, positionInVideo / this.videoDuration));
  }

  get trackPercentage() {
    const positionInAudio = this.position - this.audioOffsetFromBeginning;
    return Math.min(1.0, Math.max(0.0, positionInAudio / this.trackDuration));
  }

  get renderTrackPositionText() {
    const position = this.trackPercentage * this.trackDuration;
    const minutes = Math.floor(position / 60);
    const seconds = Math.floor(position - minutes * 60);
    if (seconds < 10) {
      return `${minutes}:0${seconds}`;
    }

    return `${minutes}:${seconds}`;
  }

  reset = () => {
    this.pause();
    this.position = this.videoAnchorPercentage * this.videoDuration;
    this.audioOffset = this.videoAnchorPercentage * this.videoDuration;
  };

  resetFromVideoSync = () => {
    this.pause();
    let adjustment = 0;
    if (this.audioOffset < 0) adjustment = Math.abs(this.audioOffset);
    this.position =
      this.videoAnchorPercentage * this.videoDuration + adjustment;
    // this.audioOffset = this.videoAnchorPercentage * this.videoDuration;
    this.play();
  };

  /**
   * @action
   * @param {number} percentage
   */
  setVideoAnchor = (percentage) => {
    this.videoAnchorPercentage = Math.min(Math.max(percentage, 0), 1);
    this.setPosition(
      this.videoAnchorPercentage * this.videoDuration +
        this.videoOffsetFromBeginning,
    );
    if (this.anchorSet) {
      this.searchState.setVideoAnchorPercentage(this.videoAnchorPercentage);
    }
  };

  /**
   * @action
   * @param {number} percentage
   */
  seekPercentage = (percentage) => {
    percentage = Math.max(0, Math.min(1, percentage));
    this.setPosition(this.duration * percentage);
  };

  /**
   * @action
   * @param {number} percentage
   */
  seekVideoPercentage = (percentage) => {
    percentage = Math.max(0, Math.min(1, percentage));
    this.setPosition(
      this.videoOffsetFromBeginning + this.videoDuration * percentage,
    );
  };

  /**
   * @action
   * @param {number} percentage
   */
  seekTrackPercentage = (percentage) => {
    percentage = Math.max(0, Math.min(1, percentage));
    this.setPosition(
      this.audioOffsetFromBeginning + this.trackDuration * percentage,
    );
  };

  /**
   * @action
   * @param {number} position
   */
  setPosition = (position) => {
    if (this.searchState.showingVideoSync) {
      this.position = Math.max(0, Math.min(position, this.duration));
    } else {
      this.position = Math.max(
        this.audioOffsetFromBeginning,
        Math.min(position, this.audioOffsetFromBeginning + this.trackDuration),
      );
    }
    this.startTime = new Date().getTime() - this.position * 1000;
    this.scrubAudioToMatchTimeline();
    this.scrubVideoToMatchTimeline();
  };

  hasAValue = (value) => {
    if (value === null || value === "" || isNaN(value)) {
      return false;
    }
    return true;
  };

  play = () => {
    if (this.playing) return;
    if (MusicTrackState.globallyActiveTrack != null) {
      if (this.firstPlay) {
        // this.firstPlay = false;
        this.reset();
      }
      const activeTrack = MusicTrackState.globallyActiveTrack;
      activeTrack.ensureLoaded(() => {
        this.playing = true;
        this.startTime = new Date().getTime() - this.position * 1000;
        if (
          this.searchState.activeTags.length === 1 &&
          !this.searchState.showingVideoSync
        ) {
          // this.searchState.showTip(
          //   "add more keywords to refine search",
          //   false,
          //   true
          // );
        }

        // console.log("this.anchorSet", this.anchorSet);

        if (MusicTrackState.globallyActiveTrack.setPositionForVideoSync) {
          this.searchState.showSyncTip = false;
          MusicTrackState.globallyActiveTrack.resetPositionForVideoSync();
          if (this.searchState.savedPlaylistData.tracks) {
            const currentSavedTrackIndex =
              this.searchState.savedPlaylistData.tracks.findIndex(
                (track) => track.id == this.searchState.selectedTrack.music.id,
              );
            const currentSavedTrack =
              this.searchState.savedPlaylistData.tracks[currentSavedTrackIndex];
            // console.log(
            //   "savedPlaylistData",
            //   this.searchState.savedPlaylistData
            // );
            // console.log("currentSavedTrack", currentSavedTrack);

            if (currentSavedTrack) {
              // this.searchState.setMusicVolume(
              //   this.hasAValue(this.searchState.savedPlaylistData.musicVolume)
              //     ? this.searchState.savedPlaylistData.musicVolume
              //     : this.searchState.musicVolume
              // );

              // this.searchState.videoSyncState.setVideoAudioLevel(
              //   this.hasAValue(this.searchState.savedPlaylistData.videoVolume)
              //     ? this.searchState.savedPlaylistData.videoVolume
              //     : this.searchState.videoSyncState.videoAudioLevel
              // );

              if (this.anchorSet === false) {
                this.setVideoAnchorPercentage(
                  this.searchState.savedPlaylistData.videoAnchorPercentage
                    ? this.searchState.savedPlaylistData.videoAnchorPercentage
                    : this.videoAnchorPercentage,
                );
                this.anchorSet = true;
              }

              if (currentSavedTrack.audioOffset != 0) {
                // console.log(
                //   "currentSavedTrack.audioOffset",
                //   currentSavedTrack.audioOffset
                // );
                this.setAudioOffset(
                  this.hasAValue(currentSavedTrack.audioOffset)
                    ? currentSavedTrack.audioOffset
                    : this.audioOffset
                      ? this.audioOffset
                      : 0,
                );
              } else {
                this.audioOffset =
                  this.videoAnchorPercentage * this.videoDuration;
              }

              this.setPosition(
                this.videoAnchorPercentage * this.videoDuration +
                  this.videoOffsetFromBeginning,
              );
            }
          }
        }

        this.firstPlay = false;

        this.scrubAudioToMatchTimeline();
        this.scrubVideoToMatchTimeline();

        this.playAnimationFrame();
        this.searchState.ensureNextTrackLoaded();
      });
    }
  };

  playAnimationFrame = () => {
    if (!this.playing) return;
    this.position = (new Date().getTime() - this.startTime) / 1000;
    let end;
    if (this.searchState.showingVideoSync) {
      end = this.duration;
    } else {
      end = this.audioOffsetFromBeginning + this.trackDuration;
    }
    if (this.position < end) {
      requestAnimationFrame(this.playAnimationFrame);
    } else {
      console.log(`REACHED END AT ${end}`);
      console.log(`Video duration: ${this.videoDuration}`);
      console.log(`Audio duration: ${this.trackDuration}`);
      console.log(`Audio offset: ${this.audioOffset}`);
      console.log(`Total duration: ${this.duration}`);
      // Stop playing
      this.position = this.duration;
      this.pause();
      this.searchState.playNextTrack();
    }
  };

  pause = () => {
    this.playing = false;
    if (MusicTrackState.globallyActiveTrack != null) {
      const activeTrack = MusicTrackState.globallyActiveTrack;
      activeTrack.stopAudio();
    }
    if (this.audioStartTimeout != null) {
      clearTimeout(this.audioStartTimeout);
    }
    if (this.videoStartTimeout != null) {
      clearTimeout(this.videoStartTimeout);
    }
    if (
      this.searchState.showingVideoSync &&
      this.searchState.videoSyncState.videoLoaded &&
      this.searchState.videoSyncState.videoRef != null
    ) {
      this.searchState.videoSyncState.videoRef.pause();
    }
  };

  scrubAudioToMatchTimeline = () => {
    // Don't scrub if we don't know the track duration yet (prevents resetting pre-load scrubs)
    if (this.trackDuration <= 0) {
      return;
    }

    if (MusicTrackState.globallyActiveTrack != null) {
      const activeTrack = MusicTrackState.globallyActiveTrack;
      const musicStartPosition = this.position - this.audioOffsetFromBeginning;
      // If the track starts after our video has been playing for a while
      if (musicStartPosition < 0) {
        activeTrack.stopAudio();
        if (this.audioStartTimeout != null) {
          clearTimeout(this.audioStartTimeout);
        }
        if (this.playing) {
          this.audioStartTimeout = setTimeout(() => {
            activeTrack.startAudio(0);
          }, -musicStartPosition * 1000);
        }
      }
      // If the track ends after our current video cursor, then no action is required
      else if (musicStartPosition >= this.trackDuration) {
        activeTrack.stopAudio();
      }
      // Otherwise, the track starts in the middle someplace
      else if (this.playing) {
        activeTrack.stopAudio();
        activeTrack.startAudio(musicStartPosition);
      }
    }
  };

  scrubVideoToMatchTimeline = () => {
    if (
      this.searchState.showingVideoSync &&
      this.searchState.videoSyncState.videoLoaded &&
      this.searchState.videoSyncState.videoRef != null
    ) {
      const videoRef = this.searchState.videoSyncState.videoRef;
      const videoStartPosition = this.position - this.videoOffsetFromBeginning;
      if (videoStartPosition < 0) {
        videoRef.currentTime = 0;
        videoRef.pause();
        if (this.videoStartTimeout != null) {
          clearTimeout(this.videoStartTimeout);
        }
        if (this.playing) {
          this.videoStartTimeout = setTimeout(() => {
            const playPromise = videoRef.play();
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                // Ignore AbortError - it's expected when pause is called quickly
                if (error.name !== "AbortError") {
                  console.error("Video play error:", error);
                }
              });
            }
          }, -videoStartPosition * 1000);
        }
      } else if (videoStartPosition >= this.videoDuration) {
        videoRef.currentTime = this.videoDuration;
        videoRef.pause();
      } else {
        videoRef.currentTime = videoStartPosition;
        if (this.playing) {
          const playPromise = videoRef.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              // Ignore AbortError - it's expected when pause is called quickly
              if (error.name !== "AbortError") {
                console.error("Video play error:", error);
              }
            });
          }
        }
      }
    }
  };
}
